import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";
import { BrandCampaign } from "../../../shared/models/brandCampaign";
import { PostLogStatus } from "../../../shared/enums/campaignPost.enums";
import { BrandPostLog } from "../../../shared/types/postLog.types";
import { BrandCampaignPostsFilters } from "../../../shared/services/filters/brand-campaign-post-filters";
import {
  BrandCampaignPostLogService,
  PostLogsFilters,
} from "../shared/services/brand-campaign-post-log.service";
import { BrandCampaignService } from "../shared/services/brand-campaign.service";

interface PostLogSummary {
  publishedPosts: number;
  publishedAds: number;
  scheduledPosts: number;
  scheduledAds: number;
}

@Component({
  selector: "app-brand-campaign-posts",
  templateUrl: "./brand-campaign-posts.component.html",
  styleUrls: ["./brand-campaign-posts.component.scss"],
})
export class BrandCampaignPostsComponent implements OnInit, OnDestroy {
  public campaign: BrandCampaign;
  @ViewChild(MatPaginator) public paginator?: MatPaginator;

  public readonly PostLogStatus = PostLogStatus;
  public readonly PAGE_SIZE = 25;
  public loading = true;
  public postLogs: BrandPostLog[] = [];
  public filters?: BrandCampaignPostsFilters;
  public filterPartnerIds = [];
  public selectedTabStatus = PostLogStatus.Published;
  public postLogSummary?: PostLogSummary;
  protected totalElements?: number;

  private offset = 0;
  private subscriptions = new Subscription();

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly brandCampaignPostLogService: BrandCampaignPostLogService,
    private readonly brandCampaignService: BrandCampaignService,
  ) {
    this.campaign = this.brandCampaignService.currentBrandCampaign;
  }

  public ngOnInit(): void {
    window.scrollTo(0, 0);
    this.loadPosts();
  }

  public ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }

  private loadPosts(): void {
    if (!this.campaign) {
      return;
    }

    this.loading = true;

    const filters: Partial<PostLogsFilters> = {
      ...this.filters,
      status: this.selectedTabStatus,
    };

    this.subscriptions.add(
      this.brandCampaignPostLogService
        .getBrandCampaignPostLogs(
          this.campaign.id,
          filters,
          this.offset,
          this.PAGE_SIZE,
        )
        .subscribe((list) => {
          this.loading = false;
          this.filterPartnerIds = [];
          this.postLogs = list.items;
          this.totalElements =
            list.items.length === 0
              ? 0
              : this.selectedTabStatus === PostLogStatus.Published
                ? list._meta.totals.publishedPosts +
                  list._meta.totals.publishedAds
                : list._meta.totals.scheduledPosts +
                  list._meta.totals.scheduledAds;

          this.postLogSummary = {
            publishedPosts: list._meta.totals.publishedPosts,
            publishedAds: list._meta.totals.publishedAds,
            scheduledPosts: list._meta.totals.scheduledPosts,
            scheduledAds: list._meta.totals.scheduledAds,
          };
        }),
    );
  }

  public onPageChange(event: PageEvent): void {
    this.offset = this.PAGE_SIZE * event.pageIndex;
    window.scrollTo(0, 0);
    this.loadPosts();
  }

  public onTabChanged(tabIndex: number): void {
    this.filters = undefined;
    this.offset = 0;
    this.selectedTabStatus =
      tabIndex === 0 ? PostLogStatus.Published : PostLogStatus.Scheduled;
    this.loadPosts();
  }

  public filterPosts(postLogsFilters: BrandCampaignPostsFilters): void {
    this.filters = postLogsFilters;
    this.paginator?.firstPage();
    this.offset = 0;
    this.loadPosts();
  }
}
